import {
  ArrowLeftIcon,
  CalendarCheckIcon,
  CalendarPlusIcon,
  CaretRightIcon,
  HandshakeIcon,
  ScalesIcon,
} from '@phosphor-icons/react'
import {
  type PlainDate,
  addDays,
  differenceInDays,
  formatMoney,
  isPlainDate,
} from '@paybae/shared'
import { useState } from 'react'
import { useNavigate } from 'react-router'

import { Card, EmptyState, SectionHeader } from '@/components/Card'
import { Pastilla } from '@/components/Editorial'
import { Comprobante } from '@/components/Comprobante'
import { ProgressRing } from '@/components/Donut'
import { ErrorState, Skeleton } from '@/components/Estado'
import {
  AvisoError,
  Campo,
  FormSheet,
  Guardar,
  Monto,
  type OpcionSegmento,
  Segmentos,
  Texto,
} from '@/components/Form'
import { FilaDetalle, HojaInferior } from '@/components/HojaInferior'
import { ScreenHeader } from '@/components/ScreenHeader'
import { ApiError, type Review, type ReviewFrequency, type ReviewsOverview } from '@/lib/api'
import { cn } from '@/lib/cn'
import { haptic } from '@/lib/haptics'
import {
  useCompletarRevision,
  useFrecuenciaRevisiones,
  useProgramarRevision,
  useRevisiones,
} from '@/lib/queries'
import { useSession } from '@/lib/session'

/**
 * Revisiones conjuntas del ahorro (§11).
 *
 * No es una función contable, es una de confianza: cada cierto tiempo el hogar
 * se sienta y comprueba que el dinero que la app dice que hay ahorrado existe
 * de verdad en el banco. Por eso el hogar de una sola persona no la ve —
 * revisarse uno mismo no demuestra nada — y por eso admite evidencia: una
 * captura del saldo vale más que un número tecleado.
 *
 * La pieza que sostiene todo es el **esperado congelado**. El servidor guarda,
 * al programar la revisión, cuánto decía la app que había ese día. Si se
 * recalculara al completarla, la comparación no probaría nada: cualquier
 * movimiento capturado entre medias movería el número contra el que se compara
 * y la revisión cuadraría siempre, por construcción. Esta pantalla nunca
 * recalcula ese total; lo muestra tal cual viene.
 *
 * La diferencia se enseña **mientras se teclea**, no después de guardar: es la
 * cifra sobre la que hay algo que hablar, y llega tarde si aparece cuando la
 * conversación ya terminó.
 */

/** Sentinela del selector: `Segmentos` trabaja con cadenas, no con nulos. */
const SIN_CADENCIA = 'sin-cadencia'

/**
 * Cada cuánto vuelve a tocar. Es el mismo salto que aplica el servidor al
 * programar la siguiente, y aquí solo sirve para proponer una fecha por defecto
 * y para dibujar el anillo.
 */
const DIAS_DE_CADENCIA: Readonly<Record<ReviewFrequency, number>> = {
  WEEKLY: 7,
  BIWEEKLY: 14,
  MONTHLY: 30,
  QUARTERLY: 91,
  SEMIANNUAL: 182,
  CUSTOM: 30,
}

const OPCIONES_CADENCIA: OpcionSegmento<string>[] = [
  { value: 'WEEKLY', label: 'Semanal', detalle: 'Cada 7 días' },
  { value: 'BIWEEKLY', label: 'Quincenal', detalle: 'Cada 14 días' },
  { value: 'MONTHLY', label: 'Mensual', detalle: 'Una vez al mes' },
  { value: 'QUARTERLY', label: 'Trimestral', detalle: 'Cada 3 meses' },
  { value: 'SEMIANNUAL', label: 'Semestral', detalle: 'Cada 6 meses' },
  // Apagar la cadencia tiene que ser posible desde la misma pantalla que la
  // enciende. Sin esta opción, activarla una vez sería una puerta de un solo
  // sentido.
  { value: SIN_CADENCIA, label: 'Sin cadencia', detalle: 'Las programamos a mano' },
]

const NOMBRE_CADENCIA: Readonly<Record<ReviewFrequency, string>> = {
  WEEKLY: 'semanal',
  BIWEEKLY: 'quincenal',
  MONTHLY: 'mensual',
  QUARTERLY: 'trimestral',
  SEMIANNUAL: 'semestral',
  CUSTOM: 'a medida',
}

export function Revisiones() {
  const revisiones = useRevisiones()
  const navigate = useNavigate()

  const volver = (): void => {
    void navigate('/ahorro')
  }

  if (revisiones.isPending) return <Cargando onVolver={volver} />

  if (revisiones.error || !revisiones.data) {
    const error = revisiones.error
    // El rol limitado no participa en la revisión del fondo común y el servidor
    // le responde 403. Decírselo así evita que intente reintentar sin fin.
    const sinPermiso = error instanceof ApiError && error.isForbidden
    return (
      <>
        <Encabezado onVolver={volver} />
        <ErrorState
          titulo={sinPermiso ? 'Esta sección no es para tu rol' : 'No pudimos cargar las revisiones'}
          mensaje={
            sinPermiso
              ? 'Las revisiones son del ahorro de todo el hogar. Pídele a quien administra que te cambie el rol si necesitas entrar.'
              : error instanceof Error
                ? error.message
                : 'Inténtalo de nuevo.'
          }
          onReintentar={sinPermiso ? undefined : () => void revisiones.refetch()}
        />
      </>
    )
  }

  const datos = revisiones.data

  // Hogar de una sola persona: la sección no se dibuja. Aquí, que es una
  // pantalla entera y alguien pudo llegar por un enlace, se explica por qué en
  // vez de dejar un hueco.
  if (!datos.available) {
    return (
      <>
        <Encabezado onVolver={volver} />
        <div className="px-safe mx-auto max-w-lg pt-1">
          <Card>
            <EmptyState
              icon={<HandshakeIcon size={32} weight="fill" />}
              title="Las revisiones son de a dos"
              description="Sentarse a comprobar que el ahorro existe de verdad solo tiene sentido cuando hay alguien más con quien comprobarlo. Invita a la otra persona al hogar desde Ajustes y esta sección se activa sola."
              action={
                <button
                  type="button"
                  onClick={() => {
                    haptic('light')
                    void navigate('/ajustes')
                  }}
                  className="flex min-h-[44px] items-center rounded-pill bg-accion px-5 text-[14px] font-semibold text-on-accion active:scale-95"
                >
                  Ir a Ajustes
                </button>
              }
            />
          </Card>
        </div>
      </>
    )
  }

  return <Panorama datos={datos} onVolver={volver} />
}

// ═══════════════════════════════════════════════════════════════════════════
//  Pantalla
// ═══════════════════════════════════════════════════════════════════════════

function Panorama({ datos, onVolver }: { datos: ReviewsOverview; onVolver: () => void }) {
  const { membership } = useSession()
  const [programando, setProgramando] = useState(false)
  const [completando, setCompletando] = useState(false)
  /** Se guarda el id, no la revisión: así la hoja sigue al dato si se refresca. */
  const [viendoId, setViendoId] = useState<string | null>(null)

  const cadencia = useFrecuenciaRevisiones()
  const esAdmin = membership.role === 'ADMIN'

  const proxima = datos.next
  const viendo = datos.history.find((r) => r.id === viendoId) ?? null

  return (
    <>
      <Encabezado
        onVolver={onVolver}
        subtitulo={`${formatMoney(datos.savedNowCents, { hideZeroCents: true })} ahorrados hoy`}
      />

      <div className="px-safe mx-auto flex max-w-lg flex-col gap-4 pt-1">
        {proxima ? (
          <TarjetaProxima
            revision={proxima}
            hoy={datos.today}
            onCompletar={() => {
              haptic('light')
              setCompletando(true)
            }}
          />
        ) : (
          <Card>
            <EmptyState
              icon={<CalendarPlusIcon size={32} weight="fill" />}
              title="No hay ninguna revisión pendiente"
              description="Pon un día para sentarse a comparar lo que la app dice que hay ahorrado contra lo que hay en el banco. Guardamos el total esperado de ese día para que la comparación signifique algo."
              action={
                <button
                  type="button"
                  onClick={() => {
                    haptic('light')
                    setProgramando(true)
                  }}
                  className="flex min-h-[44px] items-center gap-2 rounded-pill bg-accion px-5 text-[14px] font-semibold text-on-accion active:scale-95"
                >
                  <CalendarPlusIcon size={16} weight="fill" />
                  Programar revisión
                </button>
              }
            />
          </Card>
        )}

        <Card className="flex flex-col gap-3">
          <SectionHeader title="Cada cuánto toca" className="mb-0" />
          {esAdmin ? (
            <>
              <Segmentos
                opciones={OPCIONES_CADENCIA}
                value={datos.frequency ?? SIN_CADENCIA}
                onChange={(valor) => {
                  cadencia.mutate(valor === SIN_CADENCIA ? null : (valor as ReviewFrequency), {
                    onSuccess: () => haptic('success'),
                    onError: () => haptic('error'),
                  })
                }}
              />
              <p className="text-[12px] leading-snug text-ink-soft">
                Al completar una revisión programamos la siguiente con esta cadencia. Cambiarla no
                mueve la que ya está pendiente.
              </p>
              <AvisoError
                mensaje={
                  cadencia.error
                    ? mensajeDeError(cadencia.error, 'No pudimos cambiar la cadencia.')
                    : null
                }
              />
            </>
          ) : (
            <p className="text-[13px] leading-snug text-ink-soft">
              {datos.frequency
                ? `La revisión del hogar es ${NOMBRE_CADENCIA[datos.frequency]}. Quien administra el hogar puede cambiarla.`
                : 'El hogar no tiene una cadencia fija: las revisiones se programan a mano.'}
            </p>
          )}
        </Card>

        <section className="pb-2">
          <SectionHeader title="Revisiones anteriores" />
          {datos.history.length === 0 ? (
            <Card>
              <EmptyState
                icon={<ScalesIcon size={30} weight="fill" />}
                title="Todavía no han hecho ninguna"
                description="Cuando completen la primera, aquí quedará cuánto esperaban encontrar, cuánto había y en qué se diferenciaron."
              />
            </Card>
          ) : (
            <div className="flex flex-col gap-2">
              {datos.history.map((revision) => (
                <FilaHistorial
                  key={revision.id}
                  revision={revision}
                  onAbrir={() => {
                    haptic('light')
                    setViendoId(revision.id)
                  }}
                />
              ))}
            </div>
          )}
        </section>
      </div>

      {programando ? (
        <HojaProgramar
          hoy={datos.today}
          frecuencia={datos.frequency}
          esperadoCents={datos.savedNowCents}
          onCerrar={() => setProgramando(false)}
        />
      ) : null}

      {completando && proxima ? (
        <HojaCompletar revision={proxima} onCerrar={() => setCompletando(false)} />
      ) : null}

      <HojaInferior
        abierta={viendo !== null}
        titulo={viendo ? `Revisión del ${fechaCorta(viendo.scheduledFor)}` : ''}
        descripcion={
          viendo?.completedAt
            ? `Completada el ${fechaCorta(soloFecha(viendo.completedAt))}`
            : undefined
        }
        onCerrar={() => setViendoId(null)}
      >
        {viendo ? <DetalleRevision revision={viendo} /> : null}
      </HojaInferior>
    </>
  )
}

/**
 * La próxima revisión, con su cuenta regresiva.
 *
 * El anillo mide cuánto del camino entre el día en que se programó y el día
 * señalado ya se recorrió. Es la misma forma con la que se lee el avance de una
 * meta o el uso de una tarjeta en el resto de la app: una sola gramática visual
 * en vez de una barra que habría que aprender aparte.
 */
function TarjetaProxima({
  revision,
  hoy,
  onCompletar,
}: {
  revision: Review
  hoy: PlainDate
  onCompletar: () => void
}) {
  const dias = differenceInDays(revision.scheduledFor, hoy)
  const vencida = dias < 0
  const esHoy = dias === 0

  // El intervalo real —de cuándo se creó a cuándo toca— y no la cadencia del
  // hogar: una revisión programada a mano para dentro de tres días no debería
  // dibujarse como si le faltara un mes.
  const desde = soloFecha(revision.createdAt)
  const total = Math.max(1, differenceInDays(revision.scheduledFor, desde))
  const avance = vencida ? 100 : Math.min(100, Math.max(0, ((total - dias) / total) * 100))

  return (
    <Card className="flex flex-col gap-4">
      <SectionHeader title="Próxima revisión" className="mb-0" />

      <div className="flex items-center gap-4">
        <ProgressRing
          percent={avance}
          size={84}
          stroke={8}
          color={vencida || esHoy ? 'var(--warning)' : 'var(--primary)'}
        >
          <span className="flex flex-col items-center leading-none">
            <span data-money className="font-display text-[22px] font-extrabold text-ink">
              {Math.abs(dias)}
            </span>
            <span className="mt-0.5 text-[10px] font-medium text-ink-soft">
              {Math.abs(dias) === 1 ? 'día' : 'días'}
            </span>
          </span>
        </ProgressRing>

        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-semibold text-ink">{cuandoToca(dias)}</p>
          <p className="mt-0.5 text-[13px] capitalize leading-snug text-ink-soft">
            {fechaLarga(revision.scheduledFor)}
          </p>
          {revision.expectedTotalCents !== null ? (
            <p className="mt-1 text-[12px] leading-snug text-ink-faint">
              Compararán contra{' '}
              <strong data-money className="font-semibold text-ink-soft">
                {formatMoney(revision.expectedTotalCents, { hideZeroCents: true })}
              </strong>
              , el total que la app tenía el día que se programó.
            </p>
          ) : null}
        </div>
      </div>

      {revision.notes ? (
        <p className="rounded-card bg-surface-sunken px-3 py-2.5 text-[12px] leading-snug text-ink-soft">
          {revision.notes}
        </p>
      ) : null}

      <Pastilla onClick={onCompletar} icono={<CalendarCheckIcon size={18} weight="fill" />}>
        {vencida || esHoy ? 'Hacer la revisión' : 'Adelantarla y hacerla ya'}
      </Pastilla>
    </Card>
  )
}

function FilaHistorial({ revision, onAbrir }: { revision: Review; onAbrir: () => void }) {
  const diferencia = revision.differenceCents

  return (
    <button
      type="button"
      onClick={onAbrir}
      className="flex min-h-[60px] w-full items-center gap-3 rounded-card border border-line bg-surface px-3.5 py-2.5 text-left active:scale-[0.99]"
    >
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[14px] font-medium capitalize text-ink">
          {fechaLarga(revision.scheduledFor)}
        </span>
        <span className="block truncate text-[12px] text-ink-soft">
          {diferencia === null
            ? 'Sin total capturado'
            : diferencia === 0
              ? 'Cuadró exacto'
              : diferencia > 0
                ? `Sobraron ${formatMoney(diferencia, { hideZeroCents: true })}`
                : `Faltaron ${formatMoney(-diferencia, { hideZeroCents: true })}`}
          {revision.evidence.length > 0
            ? ` · ${revision.evidence.length} ${revision.evidence.length === 1 ? 'archivo' : 'archivos'}`
            : ''}
        </span>
      </span>

      {/* En tinta, siempre. El color en esta app dice de quién es el dinero, no
          si el resultado fue bueno; un rojo aquí convertiría un descuadre de
          cincuenta pesos en una alarma. */}
      <span data-money className="shrink-0 text-[14px] font-semibold text-ink">
        {revision.confirmedTotalCents !== null
          ? formatMoney(revision.confirmedTotalCents, { hideZeroCents: true })
          : '—'}
      </span>
      <CaretRightIcon size={15} weight="bold" className="shrink-0 text-ink-faint" />
    </button>
  )
}

function DetalleRevision({ revision }: { revision: Review }) {
  const diferencia = revision.differenceCents

  return (
    <>
      <FilaDetalle
        titulo="La app decía que había"
        detalle="Congelado el día que se programó"
        monto={
          <span data-money className="text-[14px] font-semibold text-ink">
            {revision.expectedTotalCents !== null
              ? formatMoney(revision.expectedTotalCents, { hideZeroCents: true })
              : '—'}
          </span>
        }
      />
      <FilaDetalle
        titulo="Lo que había de verdad"
        detalle="Comprobado en el banco"
        monto={
          <span data-money className="text-[14px] font-semibold text-ink">
            {revision.confirmedTotalCents !== null
              ? formatMoney(revision.confirmedTotalCents, { hideZeroCents: true })
              : '—'}
          </span>
        }
      />
      {diferencia !== null ? (
        <>
          <FilaDetalle
            titulo="Diferencia"
            monto={
              <span data-money className="text-[14px] font-semibold text-ink">
                {formatMoney(diferencia, { showSign: true, hideZeroCents: true })}
              </span>
            }
          />
          <p className="px-1 pt-1 text-[12px] leading-snug text-ink-soft">
            {explicacionDiferencia(diferencia)}
          </p>
        </>
      ) : null}

      {revision.notes ? (
        <p className="mt-1 rounded-card bg-surface-sunken px-3.5 py-3 text-[13px] leading-snug text-ink-soft">
          {revision.notes}
        </p>
      ) : null}

      <div className="pt-2">
        <p className="mb-2 text-[13px] font-semibold text-ink">Evidencia</p>
        <Comprobante
          destino={{ reviewId: revision.id }}
          adjuntos={revision.evidence}
          modo="evidencia"
          soloLectura
        />
      </div>
    </>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
//  Programar
// ═══════════════════════════════════════════════════════════════════════════

function HojaProgramar({
  hoy,
  frecuencia,
  esperadoCents,
  onCerrar,
}: {
  hoy: PlainDate
  frecuencia: ReviewFrequency | null
  esperadoCents: number
  onCerrar: () => void
}) {
  const programar = useProgramarRevision()

  // Se propone la fecha que tocaría con la cadencia del hogar en vez de dejar
  // el campo vacío: la mayoría de las veces es la respuesta correcta y la
  // pantalla se resuelve con un solo toque.
  const [fecha, setFecha] = useState<string>(
    addDays(hoy, DIAS_DE_CADENCIA[frecuencia ?? 'MONTHLY']),
  )
  const [nota, setNota] = useState('')

  const fechaValida = isPlainDate(fecha)
  const enElPasado = fechaValida && differenceInDays(fecha as PlainDate, hoy) < 0
  const listo = fechaValida && !enElPasado

  const guardar = (): void => {
    if (!listo || programar.isPending) return
    programar.mutate(
      { scheduledFor: fecha as PlainDate, notes: nota.trim() || null },
      {
        onSuccess: () => {
          haptic('success')
          onCerrar()
        },
        onError: () => haptic('error'),
      },
    )
  }

  return (
    <FormSheet
      tinte="mint"
      titulo="Programar revisión"
      onCerrar={onCerrar}
      accion={
        <div className="flex flex-col gap-2.5">
          <AvisoError
            mensaje={
              programar.error ? mensajeDeError(programar.error, 'No pudimos programarla.') : null
            }
          />
          <Guardar onClick={guardar} disabled={!listo} guardando={programar.isPending}>
            {enElPasado ? 'Elige una fecha de hoy en adelante' : 'Programar revisión'}
          </Guardar>
        </div>
      }
    >
      <Campo
        label="¿Qué día se sientan a revisar?"
        ayuda="Un día en que estén los dos. La app avisa cuando llegue."
        error={
          !fechaValida
            ? 'Escribe la fecha como AAAA-MM-DD.'
            : enElPasado
              ? 'Esa fecha ya pasó. La revisión se programa de hoy en adelante.'
              : undefined
        }
      >
        <input
          type="date"
          value={fecha}
          min={hoy}
          onChange={(e) => setFecha(e.target.value)}
          className="min-h-[48px] w-full rounded-card border border-line bg-surface px-3.5 text-[16px] text-ink"
        />
      </Campo>

      <p className="rounded-card bg-primary-soft px-3.5 py-3 text-[13px] leading-snug text-ink">
        Al programarla guardamos que hoy la app dice que hay{' '}
        <strong data-money className="font-semibold">
          {formatMoney(esperadoCents, { hideZeroCents: true })}
        </strong>
        . Ese es el número contra el que compararán, y no cambia aunque después
        registren movimientos: si se recalculara el día de la revisión, siempre
        cuadraría y no probaría nada.
      </p>

      <Campo
        label="Nota para ese día (opcional)"
        ayuda="Qué quieren revisar además del total. Lo verán los dos al abrir el aviso."
      >
        <Texto
          value={nota}
          onChange={setNota}
          placeholder="Checar también la cuenta de la boda"
          maxLength={1000}
        />
      </Campo>
    </FormSheet>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
//  Completar
// ═══════════════════════════════════════════════════════════════════════════

function HojaCompletar({ revision, onCerrar }: { revision: Review; onCerrar: () => void }) {
  const completar = useCompletarRevision()

  const [confirmadoCents, setConfirmadoCents] = useState(0)
  const [nota, setNota] = useState(revision.notes ?? '')
  const [tocado, setTocado] = useState(false)

  const esperado = revision.expectedTotalCents
  // Sin haber tocado el teclado no hay respuesta todavía, y un cero de fábrica
  // no es «no hay nada»: mostrar la diferencia de entrada acusaría de un
  // faltante que nadie ha capturado.
  const diferencia = tocado && esperado !== null ? confirmadoCents - esperado : null

  const escribirMonto = (siguiente: React.SetStateAction<number>): void => {
    setTocado(true)
    setConfirmadoCents(siguiente)
  }

  const guardar = (): void => {
    if (!tocado || completar.isPending) return
    completar.mutate(
      { id: revision.id, confirmedTotalCents: confirmadoCents, notes: nota.trim() || null },
      {
        onSuccess: () => {
          haptic('success')
          onCerrar()
        },
        onError: () => haptic('error'),
      },
    )
  }

  return (
    <FormSheet
      tinte="mint"
      titulo="Revisión del ahorro"
      onCerrar={onCerrar}
      accion={
        <div className="flex flex-col gap-2.5">
          <AvisoError
            mensaje={
              completar.error ? mensajeDeError(completar.error, 'No pudimos guardarla.') : null
            }
          />
          <Guardar onClick={guardar} disabled={!tocado} guardando={completar.isPending}>
            {tocado ? 'Cerrar la revisión' : 'Escribe cuánto hay de verdad'}
          </Guardar>
        </div>
      }
    >
      {/* Primero lo que la app dice, después lo que hay. En ese orden porque la
          pregunta que se responde es «¿coincide?», y sin el punto de partida a
          la vista el número que se teclea no significa nada. */}
      <div className="flex flex-col gap-1 rounded-card border border-line bg-surface p-3.5">
        <span className="text-[12px] font-medium uppercase tracking-[0.06em] text-ink-soft">
          La app dice que deberías tener
        </span>
        <span
          data-money
          className="font-display text-[30px] font-extrabold leading-none tracking-[-0.03em] text-ink"
        >
          {esperado !== null ? formatMoney(esperado, { hideZeroCents: true }) : '—'}
        </span>
        <span className="text-[12px] leading-snug text-ink-faint">
          Es el total que la app tenía guardado el día que se programó esta revisión, no el de hoy.
        </span>
      </div>

      <Campo
        label="¿Cuánto hay de verdad?"
        ayuda="Suma los saldos reales de donde tengan el ahorro y escríbelos aquí."
      >
        <Monto cents={confirmadoCents} onChange={escribirMonto} />
      </Campo>

      {esperado !== null && esperado > 0 && confirmadoCents !== esperado ? (
        <button
          type="button"
          onClick={() => {
            haptic('selection')
            escribirMonto(esperado)
          }}
          className="flex min-h-[44px] items-center justify-center rounded-pill border border-line bg-surface px-4 text-[13px] font-semibold text-ink active:scale-[0.98]"
        >
          Es justo esa cantidad
        </button>
      ) : null}

      {diferencia !== null ? <ResultadoDiferencia diferencia={diferencia} /> : null}

      <Campo
        label="Nota (opcional)"
        ayuda="A qué se debió la diferencia, o qué acordaron. Es lo que se lee dentro de seis meses."
      >
        <Texto
          value={nota}
          onChange={setNota}
          placeholder="Se sacaron $2,000 para la reparación del coche"
          maxLength={1000}
        />
      </Campo>

      <Campo
        label="Evidencia (opcional)"
        ayuda="La captura del saldo, el estado de cuenta en PDF o el Excel del banco."
      >
        {/* Se puede adjuntar antes de cerrar porque la revisión ya existe: se
            creó al programarla, así que tiene id desde el primer día. */}
        <Comprobante
          destino={{ reviewId: revision.id }}
          adjuntos={revision.evidence}
          modo="evidencia"
        />
      </Campo>
    </FormSheet>
  )
}

/**
 * La diferencia, en cuanto hay un número que comparar.
 *
 * El monto va en tinta y la explicación en una caja tintada. El descuadre no se
 * pinta de rojo: en esta app el color dice de quién es el dinero, y teñir de
 * alarma un faltante de cincuenta pesos entrena a ignorar el aviso el día que
 * de verdad falten cinco mil. El ámbar de la caja llama la atención sin juzgar.
 */
function ResultadoDiferencia({ diferencia }: { diferencia: number }) {
  const cuadra = diferencia === 0

  return (
    <div
      role="status"
      className={cn(
        'flex flex-col gap-1 rounded-card px-3.5 py-3',
        cuadra ? 'bg-surface-sunken' : 'bg-warning-soft',
      )}
    >
      <span className="text-[12px] font-medium uppercase tracking-[0.06em] text-ink-soft">
        {cuadra ? 'Cuadra' : diferencia > 0 ? 'Sobra' : 'Falta'}
      </span>
      <span
        data-money
        className="font-display text-[26px] font-extrabold leading-none tracking-[-0.03em] text-ink"
      >
        {formatMoney(Math.abs(diferencia), { hideZeroCents: true })}
      </span>
      <span className="text-[12px] leading-snug text-ink-soft">
        {explicacionDiferencia(diferencia)}
      </span>
    </div>
  )
}

/** Una línea que diga qué significa el número, no solo cuál es. */
function explicacionDiferencia(diferencia: number): string {
  if (diferencia === 0) {
    return 'Lo que la app tenía registrado es exactamente lo que hay. No falta ni sobra un peso.'
  }
  if (diferencia > 0) {
    return 'Hay más de lo que la app tenía registrado: casi siempre es un depósito, unos intereses o una aportación que nadie capturó.'
  }
  return 'Hay menos de lo que la app tenía registrado: casi siempre es un retiro del ahorro que no se registró como movimiento.'
}

// ═══════════════════════════════════════════════════════════════════════════
//  Piezas comunes
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Encabezado con salida hacia Ahorro.
 *
 * La vuelta va a la derecha y no arriba a la izquierda: en un teléfono grande
 * esa esquina es la más lejana del pulgar, y esta pantalla se abre desde Ahorro
 * con una mano.
 */
function Encabezado({ onVolver, subtitulo }: { onVolver: () => void; subtitulo?: string }) {
  return (
    <ScreenHeader
      title="Revisiones"
      tinte="mint"
      subtitle={subtitulo}
      action={
        <button
          type="button"
          onClick={() => {
            haptic('light')
            onVolver()
          }}
          className="flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-pill border border-line bg-surface px-3.5 text-[13px] font-semibold text-ink active:scale-95"
        >
          <ArrowLeftIcon size={15} weight="bold" />
          Ahorro
        </button>
      }
    />
  )
}

function Cargando({ onVolver }: { onVolver: () => void }) {
  return (
    <>
      <Encabezado onVolver={onVolver} subtitulo="Revisando el fondo…" />
      <div className="px-safe mx-auto flex max-w-lg flex-col gap-4 pt-1">
        <Card className="flex flex-col gap-4">
          <div className="flex h-[22px] items-center">
            <Skeleton className="h-4 w-36" />
          </div>
          <div className="flex items-center gap-4">
            <Skeleton className="size-[84px] rounded-full" />
            <div className="min-w-0 flex-1">
              <div className="flex h-[22px] items-center">
                <Skeleton className="h-3.5 w-28" />
              </div>
              <div className="mt-0.5 flex h-[20px] items-center">
                <Skeleton className="h-3 w-40" />
              </div>
              <div className="mt-1 flex h-[18px] items-center">
                <Skeleton className="h-3 w-44" />
              </div>
            </div>
          </div>
          <Skeleton className="h-[48px]" />
        </Card>

        <Card className="flex flex-col gap-3">
          <div className="flex h-[22px] items-center">
            <Skeleton className="h-4 w-32" />
          </div>
          <Skeleton className="h-[164px]" />
        </Card>
      </div>
    </>
  )
}

/** Fecha de un sello de tiempo del servidor, en formato de calendario. */
function soloFecha(iso: string): PlainDate {
  return iso.slice(0, 10) as PlainDate
}

/**
 * `PlainDate` a `Date`, siempre en UTC.
 *
 * Sin `Date.UTC` el navegador interpretaría la fecha en su huso y un `2026-08-20`
 * se convertiría en el 19 al oeste de Greenwich: la revisión aparecería un día
 * antes de lo que dice el servidor.
 */
function aFecha(fecha: PlainDate): Date {
  return new Date(Date.UTC(Number(fecha.slice(0, 4)), Number(fecha.slice(5, 7)) - 1, Number(fecha.slice(8, 10))))
}

function fechaLarga(fecha: PlainDate): string {
  return aFecha(fecha).toLocaleDateString('es-MX', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  })
}

function fechaCorta(fecha: PlainDate): string {
  return aFecha(fecha).toLocaleDateString('es-MX', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

function cuandoToca(dias: number): string {
  if (dias < -1) return `Se pasó hace ${-dias} días`
  if (dias === -1) return 'Se pasó ayer'
  if (dias === 0) return 'Toca hoy'
  if (dias === 1) return 'Toca mañana'
  return `Faltan ${dias} días`
}

/** El detalle por campo que manda el servidor dice qué corregir; un genérico no. */
function mensajeDeError(error: unknown, respaldo: string): string {
  if (error instanceof ApiError) {
    const detalles = Object.values(error.fieldErrors ?? {}).flat()
    return detalles.length > 0 ? detalles.join('. ') : error.message
  }
  return respaldo
}
